import { type FormEvent, useEffect, useRef, useState } from 'react';
import { ArrowDownRight, ArrowLeft, ArrowRight, BellRing, Check, ChevronDown, Inbox, Languages, Menu, Moon, Sparkles, Sun, X } from 'lucide-react';
import { company } from './config/company';
import { StudioLegalPage } from './components/StudioLegalPage';
import { studioLegalCopy } from './studio-legal-copy';
import { isLanguage, type Language } from './i18n';
import { studioCopy } from './studio-copy';
import { interactiveCopy } from './studio-interactive-copy';
import { StudioFaq, ThemeToggle, type StudioTheme } from './components/StudioInteractive';
import { PlanBuilder } from './components/ProjectConfigurator';
import { builderCopy } from './studio-builder-copy';
import { sendContactRequest } from './lib/contact-request';
import { FormFieldError, useLocalizedForm } from './components/LocalizedForm';
import { ProcessCalculator } from './components/ProcessCalculator';
import { FlowMateDemo } from './components/FlowMateDemo';
import { FlowMatePricing } from './components/FlowMatePricing';
import { flowmateCopy } from './studio-flowmate-copy';
import { StudioBrand } from './components/StudioBrand';
import { PrivacyControls } from './components/PrivacyControls';
import { trackAnalytics } from './studio-analytics';

const storageKey = 'asystent-remontowy-ai-language';
const languageLabels: Record<Language, { code: string; name: string }> = {
  pl: { code: 'PL', name: 'Polski' },
  en: { code: 'EN', name: 'English' },
  uk: { code: 'UA', name: 'Українська' },
  ru: { code: 'RU', name: 'Русский' }
};

function getInitialLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(storageKey);
    return isLanguage(stored) ? stored : 'pl';
  } catch { return 'pl'; }
}

function getInitialTheme(): StudioTheme {
  try { return window.localStorage.getItem('studio-theme') === 'dark' ? 'dark' : 'light'; }
  catch { return 'light'; }
}

function Contact({ language }: { language: Language }) {
  const c = studioCopy[language].contact;
  const [state, setState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const inFlight = useRef(false);
  const validation = useLocalizedForm(language);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (inFlight.current || !validation.validate(form)) return;
    const data = new FormData(form);
    inFlight.current = true;
    setState('sending');
    try {
      await sendContactRequest(company.formAction, data);
      trackAnalytics('contact_submit');
      form.reset();
      validation.reset();
      setState('success');
    } catch { setState('error'); }
    finally { inFlight.current = false; }
  }
  return <section className="studio-contact" id="contact"><div className="studio-wrap studio-contact__grid">
    <div data-reveal><span className="studio-kicker">{c.label}</span><h2>{c.title}</h2><p>{c.text}</p>
      <a className="studio-contact__email" href={`mailto:${company.email}`}>{company.email}<ArrowDownRight size={20} /></a>
    </div>
    <form className="studio-form" onSubmit={submit} onInput={validation.onInput} noValidate aria-busy={state === 'sending'} data-reveal>
      <input type="hidden" name="_subject" value="Nowe zapytanie - Odaro Studio" />
      {Object.keys(validation.errors).length > 0 && <p className="studio-validation-summary" role="alert">{validation.summary}</p>}
      <div className="studio-form-field"><label htmlFor="contact-name">{c.name}</label><input id="contact-name" name="name" autoComplete="name" required maxLength={100} disabled={state === 'sending'} aria-invalid={Boolean(validation.errors.name)} aria-describedby={validation.errors.name ? 'contact-name-error' : undefined} /><FormFieldError id="contact-name-error" message={validation.errorFor('name')} /></div>
      <div className="studio-form-field"><label htmlFor="contact-email">{c.email}</label><input id="contact-email" name="email" type="email" autoComplete="email" required maxLength={254} disabled={state === 'sending'} aria-invalid={Boolean(validation.errors.email)} aria-describedby={validation.errors.email ? 'contact-email-error' : undefined} /><FormFieldError id="contact-email-error" message={validation.errorFor('email')} /></div>
      <div className="studio-form-field"><label htmlFor="contact-message">{c.message}</label><textarea id="contact-message" name="message" required minLength={10} maxLength={4000} rows={6} disabled={state === 'sending'} aria-invalid={Boolean(validation.errors.message)} aria-describedby={validation.errors.message ? 'contact-message-error' : undefined} /><FormFieldError id="contact-message-error" message={validation.errorFor('message')} /></div>
      <div className="studio-form__foot"><a href="/privacy">{c.privacy}</a><button className="studio-button studio-button--dark" disabled={state === 'sending'} type="submit">{state === 'sending' ? c.sending : c.send}<ArrowRight size={18} /></button></div>
      {state === 'success' && <p className="studio-form__status" role="status">{c.success}</p>}
      {state === 'error' && <p className="studio-form__status studio-form__status--error" role="alert">{c.error}</p>}
    </form>
  </div></section>;
}

function Home({ language }: { language: Language }) {
  const c = studioCopy[language];
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  return <main>
    <section className="studio-hero" id="top">
      <span className="studio-hero__watermark" aria-hidden="true">o.</span>
      <div className="studio-wrap studio-hero__inner">
        <div className="studio-hero__main"><span className="studio-kicker">{c.hero.eyebrow}</span><h1>{c.hero.statement}<span className="studio-hero__period">.</span></h1><p>{c.hero.detail}</p>
          <div className="studio-hero__actions"><a className="studio-button studio-button--dark" href="#contact">{c.hero.primary}<ArrowRight size={18} /></a><a className="studio-text-link" href="/flowmate">{c.hero.secondary}<ArrowDownRight size={18} /></a></div>
        </div>
        <div className="studio-hero__rail" aria-label={c.hero.stages.join(' → ')}><span className="studio-hero__progress" aria-hidden="true" />{c.hero.stages.map((stage, index) => <span className="studio-hero__stage" key={stage}><i>0{index + 1}</i>{stage}</span>)}</div>
      </div>
    </section>

    <section className="studio-intro" data-reveal><div className="studio-wrap studio-intro__grid"><span className="studio-kicker">{c.intro.label}</span><div><h2>{c.intro.title}</h2><p>{c.intro.text}</p></div></div></section>

    <section className="studio-services" id="services"><div className="studio-wrap">
      <div className="studio-section-head" data-reveal><span className="studio-kicker">{c.services.label}</span><h2>{c.services.title}</h2></div>
      <div className="studio-services__list">{c.services.items.map((item) => <div className="studio-service" key={item.number} data-reveal><span>{item.number}</span><h3>{item.title}</h3><p>{item.body}</p><ArrowDownRight size={21} aria-hidden="true" /></div>)}</div>
    </div></section>

    <ProcessCalculator language={language} onStart={() => setSelectedServices((current) => current.includes('automation') ? current : [...current, 'automation'])} />

    <PlanBuilder language={language} selected={selectedServices} onChange={setSelectedServices} />

    <section className="studio-work" id="work"><div className="studio-wrap studio-work__layout">
      <div className="studio-work__copy" data-reveal><span className="studio-kicker">{c.work.label}</span><h2>{c.work.title}</h2><p>{c.work.text}</p><FlowMatePricing language={language} compact /><a className="studio-text-link" href="/flowmate">{c.work.link}<ArrowRight size={19} /></a></div>
      <FlowMateDemo language={language} />
    </div></section>

    <section className="studio-method" id="method"><div className="studio-wrap">
      <div className="studio-section-head" data-reveal><span className="studio-kicker">{c.method.label}</span><h2>{c.method.title}</h2></div>
      <ol className="studio-method__steps">{c.method.steps.map((step, index) => <li key={step.title} data-reveal><span>0{index + 1}</span><div><h3>{step.title}</h3><p>{step.body}</p></div></li>)}</ol>
    </div></section>

    <StudioFaq language={language} />
    <section className="studio-about"><div className="studio-wrap studio-about__layout" data-reveal><span className="studio-kicker">{c.about.label}</span><div><h2>{c.about.title}</h2><p>{c.about.text}</p><span className="studio-about__signature">Odaro</span></div></div></section>
    <Contact language={language} />
  </main>;
}

function FlowMate({ language }: { language: Language }) {
  const c = studioCopy[language].flowmate;
  const value = flowmateCopy[language];
  const icons = [Languages, Inbox, Sparkles, BellRing];
  return <main>
    <section className="studio-product-hero"><div className="studio-wrap"><a className="studio-back" href="/"><ArrowLeft size={18} />{c.back}</a><span className="studio-kicker">{c.label}</span><h1>{c.title}<span className="studio-hero__period">.</span></h1><p>{c.intro}</p></div></section>
    <section className="studio-product"><div className="studio-wrap">
      <div className="studio-product__lead" data-reveal><h2>{value.valueTitle}</h2><p>{value.valueIntro}</p></div>
      <div className="studio-product__benefits">{value.benefits.map((benefit, index) => { const Icon = icons[index]; return <div key={benefit.title} data-reveal><Icon size={23} aria-hidden="true" /><h3>{benefit.title}</h3><p>{benefit.body}</p></div>; })}</div>
      <div className="studio-product__demo" id="flowmate-demo"><h2>{value.demoTitle}</h2><FlowMateDemo language={language} initialView={1} /></div>
    </div></section>
    <FlowMatePricing language={language} />
    <Contact language={language} />
  </main>;
}

export default function StudioApp() {
  const [language, setLanguage] = useState<Language>(getInitialLanguage);
  const [theme, setTheme] = useState<StudioTheme>(getInitialTheme);
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const isTerms = path === '/terms';
  const isPrivacy = path === '/privacy' || isTerms;
  const isFlowMate = path === '/flowmate';
  const c = studioCopy[language];
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    try { window.localStorage.setItem('studio-theme', theme); } catch { /* Storage can be disabled in private browsers. */ }
  }, [theme]);
  useEffect(() => {
    try { window.localStorage.setItem(storageKey, language); } catch { /* Preferences are optional when browser storage is disabled. */ }
    document.documentElement.lang = language;
    document.title = isPrivacy ? `${isTerms ? studioLegalCopy[language].terms : c.footer.privacy} | Odaro Studio` : isFlowMate ? 'FlowMate | Odaro Studio' : 'Odaro Studio | Aplikacje i automatyzacje';
    document.querySelector('meta[name="description"]')?.setAttribute('content', isFlowMate ? c.flowmate.intro : c.hero.detail);
  }, [c, isFlowMate, isPrivacy, isTerms, language]);
  useEffect(() => {
    if (!('IntersectionObserver' in window)) { document.querySelectorAll('[data-reveal]').forEach((element) => element.classList.add('is-visible')); return; }
    const observer = new IntersectionObserver((entries) => { for (const entry of entries) if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); } }, { rootMargin: '0px 0px -48px 0px', threshold: 0.05 });
    document.querySelectorAll('[data-reveal]').forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [isFlowMate, isPrivacy]);
  useEffect(() => {
    if (isFlowMate || isPrivacy) return;
    let frame = 0;
    function updateSection() {
      frame = 0;
      const marker = (headerRef.current?.offsetHeight ?? 70) + Math.min(window.innerHeight * .2, 160);
      let active: string | null = null;
      for (const id of ['services', 'plan', 'work', 'method', 'contact']) {
        const section = document.getElementById(id);
        if (section && section.getBoundingClientRect().top <= marker) active = id;
      }
      setActiveSection(active);
    }
    function scheduleUpdate() { if (!frame) frame = window.requestAnimationFrame(updateSection); }
    updateSection();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      window.cancelAnimationFrame(frame);
    };
  }, [isFlowMate, isPrivacy]);
  useEffect(() => {
    if (isFlowMate || isPrivacy || !window.location.hash) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    const frame = window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    return () => window.cancelAnimationFrame(frame);
  }, [isFlowMate, isPrivacy]);
  useEffect(() => {
    function closeOutside(event: PointerEvent) {
      const target = event.target as Node;
      if (!languageRef.current?.contains(target)) setLanguageOpen(false);
      if (!headerRef.current?.contains(target)) setMenuOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') { setLanguageOpen(false); setMenuOpen(false); }
    }
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => { document.removeEventListener('pointerdown', closeOutside); document.removeEventListener('keydown', closeOnEscape); };
  }, []);
  const navBase = isFlowMate || isPrivacy ? '/' : '';
  const nav = [{ id: 'services', text: c.nav.services, href: `${navBase}#services` }, { id: 'plan', text: interactiveCopy[language].plan.label, href: `${navBase}#plan` }, { id: 'work', text: c.nav.work, href: `${navBase}#work` }, { id: 'method', text: c.nav.method, href: `${navBase}#method` }, { id: 'contact', text: c.nav.contact, href: `${navBase}#contact` }];
  return <>
    <header className="studio-header" ref={headerRef}><div className="studio-wrap studio-header__inner">
      <a className="studio-brand" href="/" aria-label={company.serviceName}><StudioBrand /></a>
      <nav className={`studio-nav${menuOpen ? ' studio-nav--open' : ''}`} aria-label={c.nav.menu} id="studio-navigation">{nav.map((item) => <a className={activeSection === item.id ? 'studio-nav__active' : ''} href={item.href} key={item.id} onClick={() => { setMenuOpen(false); setActiveSection(item.id); }}>{item.text}{item.id === 'contact' && <ArrowRight size={16} />}</a>)}</nav>
      <div className="studio-header__tools"><button className="studio-theme-icon" type="button" title={builderCopy[language].theme} aria-label={builderCopy[language].theme} aria-pressed={theme === 'dark'} onClick={() => setTheme((value) => value === 'light' ? 'dark' : 'light')}>{theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />}</button><div className="studio-language" ref={languageRef}>
        <button className="studio-language__trigger" type="button" aria-label={`Language: ${languageLabels[language].name}`} aria-controls="studio-language-options" aria-expanded={languageOpen} onClick={() => { setLanguageOpen(!languageOpen); setMenuOpen(false); }}><span>{languageLabels[language].code}</span><ChevronDown size={15} aria-hidden="true" /></button>
        <div className={`studio-language__menu${languageOpen ? ' studio-language__menu--open' : ''}`} id="studio-language-options" role="group" aria-label="Language" inert={!languageOpen}>{(Object.keys(languageLabels) as Language[]).map((option) => <button key={option} type="button" aria-pressed={language === option} onClick={() => { setLanguage(option); setLanguageOpen(false); }}><span className="studio-language__code">{languageLabels[option].code}</span><span>{languageLabels[option].name}</span>{language === option && <Check size={16} aria-hidden="true" />}</button>)}</div>
      </div><button className="studio-menu-button" type="button" aria-label={c.nav.menu} aria-controls="studio-navigation" aria-expanded={menuOpen} onClick={() => { setMenuOpen(!menuOpen); setLanguageOpen(false); }}>{menuOpen ? <X size={22} /> : <Menu size={22} />}</button></div>
    </div></header>
    {isPrivacy ? <StudioLegalPage language={language} terms={isTerms} /> : isFlowMate ? <FlowMate language={language} /> : <Home language={language} />}
    <footer className="studio-footer"><div className="studio-wrap studio-footer__top"><span className="studio-brand"><StudioBrand /></span><p>{c.footer.line}</p><div className="studio-footer__tools"><ThemeToggle language={language} theme={theme} onToggle={() => setTheme((value) => value === 'light' ? 'dark' : 'light')} /><a className="studio-footer__top-link" href="#root">{builderCopy[language].top}<ArrowRight size={16} /></a></div></div><div className="studio-wrap studio-footer__bottom"><span>© {new Date().getFullYear()} {company.serviceName}. {c.footer.rights}</span><div><a href="/flowmate">FlowMate</a><a href="/privacy">{c.footer.privacy}</a><a href="/terms">{studioLegalCopy[language].terms}</a><PrivacyControls language={language} /><a href={`mailto:${company.email}`}>{company.email}</a></div></div></footer>
  </>;
}
